import { NgModule } from '@angular/core';
import { ActivatedRouteSnapshot, RouterModule } from '@angular/router';

import {
  DprAttachmentsComponent,
  DprCommentComponent,
  DprConsumablesGridComponent,
  DprHseRegistrationsGridComponent,
  DprPersonnelRegistrationsGridComponent,
  DprProgressGridComponent,
  DprProgressWithoutTargetGridComponent,
  DprCommentAttachmentsComponent,
  DprWeatherRegistrationsGridComponent,
  DprTableOfColumnsRegistrationsGridComponent,
} from './components';
import {
  DprActivitiesGridContainerComponent,
  DprActivitiesPageComponent,
  DprConsumablesPageComponent,
  DprDetailsPageComponent,
  DprHsePageComponent,
  DprWeatherPageComponent,
  DprPersonnelPageComponent,
  DprSummaryPageComponent,
  DprProgressPageComponent,
  DprProgressWithoutTargetPageComponent,
  DprAppendixPageComponent,
  DprForecastActivitiesPageComponent,
  DprHiddenPageComponent,
  DprStatusTrackerPageComponent,
  DprStatusTrackerGridContainerComponent,
  DprTableOfColumnsPageComponent,
  DprFreeTextFieldsPageComponent,
} from './containers';
import { canActivateTabGuard } from './guards';
import { DprDetailsRoutes } from './models';
import { canAccessFreeTextFields, canAccessTableOfColumns } from './utils';
import { canDeactivateUnsavedChangesGuard } from '@shared/guards';

const routes: DprDetailsRoutes = [
  {
    path: ':dprDetailId',
    component: DprDetailsPageComponent,
    children: [
      { path: '', pathMatch: 'full', redirectTo: 'activities' },
      {
        path: 'activities',
        component: DprActivitiesPageComponent,
        children: [
          {
            path: ':activityStreamId',
            canDeactivate: [canDeactivateUnsavedChangesGuard],
            component: DprActivitiesGridContainerComponent,
            // we need this so that the unsaved changes guard is not triggered when the id is changed
            runGuardsAndResolvers: (
              from: ActivatedRouteSnapshot,
              to: ActivatedRouteSnapshot
            ) =>
              from.params.activityStreamId === to.params.activityStreamId &&
              from.queryParams?.modal == null &&
              to.queryParams?.modal == null,
          },
        ],
      },
      {
        path: 'status-tracker',
        component: DprStatusTrackerPageComponent,
        canActivate: [canActivateTabGuard],
        children: [
          {
            path: ':statusTrackerId',
            canDeactivate: [canDeactivateUnsavedChangesGuard],
            component: DprStatusTrackerGridContainerComponent,
            // we need this so that the unsaved changes guard is not triggered when the id is changed
            runGuardsAndResolvers: (
              from: ActivatedRouteSnapshot,
              to: ActivatedRouteSnapshot
            ) =>
              from.params.statusTrackerId === to.params.statusTrackerId &&
              from.queryParams?.modal == null &&
              to.queryParams?.modal == null,
          },
        ],
        data: {
          canAccess: 'enableStatusTrackerTab',
        },
      },
      {
        path: 'weather',
        component: DprWeatherPageComponent,
        canDeactivate: [canDeactivateUnsavedChangesGuard],
        children: [
          { path: '', pathMatch: 'full', redirectTo: 'registrations' },
          {
            path: 'registrations',
            component: DprWeatherRegistrationsGridComponent,
          },
          {
            path: 'attachments',
            component: DprAttachmentsComponent,
          },
          {
            path: 'comment',
            component: DprCommentComponent,
          },
        ],
      },
      {
        path: 'hse',
        component: DprHsePageComponent,
        canDeactivate: [canDeactivateUnsavedChangesGuard],
        children: [
          { path: '', pathMatch: 'full', redirectTo: 'registrations' },
          {
            path: 'registrations',
            component: DprHseRegistrationsGridComponent,
          },
          {
            path: 'attachments',
            component: DprAttachmentsComponent,
          },
          {
            path: 'comment',
            component: DprCommentComponent,
          },
        ],
      },
      {
        path: 'personnel',
        component: DprPersonnelPageComponent,
        canDeactivate: [canDeactivateUnsavedChangesGuard],
        children: [
          { path: '', pathMatch: 'full', redirectTo: 'registrations' },
          {
            path: 'registrations',
            component: DprPersonnelRegistrationsGridComponent,
          },
          {
            path: 'attachments',
            component: DprAttachmentsComponent,
          },
          {
            path: 'comment',
            component: DprCommentComponent,
          },
        ],
      },
      {
        path: 'consumables',
        component: DprConsumablesPageComponent,
        canDeactivate: [canDeactivateUnsavedChangesGuard],
        children: [
          { path: '', pathMatch: 'full', redirectTo: 'registrations' },
          {
            path: 'registrations',
            component: DprConsumablesGridComponent,
          },
          {
            path: 'attachments',
            component: DprAttachmentsComponent,
          },
          {
            path: 'comment',
            component: DprCommentComponent,
          },
        ],
      },
      {
        path: 'summary',
        component: DprSummaryPageComponent,
        canDeactivate: [canDeactivateUnsavedChangesGuard],
        children: [
          { path: '', pathMatch: 'full', redirectTo: 'current-dpr-summary' },
          {
            path: 'current-dpr-summary',
            component: DprCommentAttachmentsComponent,
          },
          {
            path: 'next-24h-planning',
            component: DprCommentAttachmentsComponent,
          },
          {
            path: 'external-party-comment',
            component: DprCommentComponent,
          },
        ],
      },
      {
        path: 'forecast',
        component: DprForecastActivitiesPageComponent,
        canActivate: [canActivateTabGuard],
        canDeactivate: [canDeactivateUnsavedChangesGuard],
        data: {
          canAccess: 'enableForecast',
        },
      },
      {
        path: 'appendix',
        component: DprAppendixPageComponent,
        canActivate: [canActivateTabGuard],
        canDeactivate: [canDeactivateUnsavedChangesGuard],
        data: {
          canAccess: 'enableAppendix',
        },
      },
      {
        path: 'free-text-fields/:freeTextFieldId',
        component: DprFreeTextFieldsPageComponent,
        canActivate: [canActivateTabGuard],
        canDeactivate: [canDeactivateUnsavedChangesGuard],
        data: {
          shouldRecomputeOnParamsChange: true,
          canAccess: canAccessFreeTextFields,
        },
        children: [
          { path: '', pathMatch: 'full', redirectTo: '' },
          {
            path: ':subtabId',
            canDeactivate: [canDeactivateUnsavedChangesGuard],
            component: DprCommentComponent,
          },
        ],
      },
      {
        path: 'progress-count-with-target',
        component: DprProgressPageComponent,
        canActivate: [canActivateTabGuard],
        canDeactivate: [canDeactivateUnsavedChangesGuard],
        data: {
          canAccess: 'enableProgressTab',
        },
        children: [
          { path: '', pathMatch: 'full', redirectTo: 'registrations' },
          {
            path: 'registrations',
            component: DprProgressGridComponent,
          },
          {
            path: 'attachments',
            component: DprAttachmentsComponent,
          },
          {
            path: 'comment',
            component: DprCommentComponent,
          },
        ],
      },
      {
        path: 'progress-count-without-target',
        component: DprProgressWithoutTargetPageComponent,
        canActivate: [canActivateTabGuard],
        canDeactivate: [canDeactivateUnsavedChangesGuard],
        data: {
          canAccess: 'enableProgressWithoutTargetTab',
        },
        children: [
          { path: '', pathMatch: 'full', redirectTo: 'registrations' },
          {
            path: 'registrations',
            component: DprProgressWithoutTargetGridComponent,
          },
          {
            path: 'attachments',
            component: DprAttachmentsComponent,
          },
          {
            path: 'comment',
            component: DprCommentComponent,
          },
        ],
      },
      {
        path: 'tables-of-columns/:tableOfColumnId',
        component: DprTableOfColumnsPageComponent,
        canActivate: [canActivateTabGuard],
        canDeactivate: [canDeactivateUnsavedChangesGuard],
        data: {
          shouldRecomputeOnParamsChange: true,
          canAccess: canAccessTableOfColumns,
        },
        children: [
          { path: '', pathMatch: 'full', redirectTo: 'registrations' },
          {
            path: 'registrations',
            component: DprTableOfColumnsRegistrationsGridComponent,
          },
          {
            path: 'attachments',
            component: DprAttachmentsComponent,
          },
          {
            path: 'comment',
            component: DprCommentComponent,
          },
        ],
      },
      {
        path: 'hidden-free-text-fields',
        component: DprHiddenPageComponent,
        canActivate: [canActivateTabGuard],
        canDeactivate: [canDeactivateUnsavedChangesGuard],
        data: {
          canAccess: 'enableHiddenTab',
        },
        children: [
          { path: '', pathMatch: 'full', redirectTo: '' },
          {
            path: ':subtabId',
            canDeactivate: [canDeactivateUnsavedChangesGuard],
            component: DprCommentAttachmentsComponent,
          },
        ],
      },
    ],
  },
];

@NgModule({
  imports: [RouterModule.forChild(routes)],
  exports: [RouterModule],
})
export class DprDetailsRoutingModule {}
